import {defineStore} from 'pinia'
import {computed, ref, shallowRef, watch} from 'vue'
import {useEventListener, useLocalStorage} from '@vueuse/core'
import {
  createLocalAudioTrack,
  createLocalVideoTrack,
  LocalAudioTrack,
  LocalVideoTrack,
  Track,
} from 'livekit-client'
import {
  createStabilizedBackgroundProcessor,
  supportsStabilizedBackgroundProcessor,
} from '@toolify/client/src/modules/call/processors/createStabilizedBackgroundProcessor'
import {CallVideoFilterType} from '@toolify/client/src/modules/call/enum/CallVideoFilterType'
import {CallLayoutType} from '@toolify/client/src/modules/call/enum/CallLayoutType'
import {useCallBackgroundStore} from '@toolify/client/src/stores/CallBackgroundStore/useCallBackgroundStore'

type MaybeAudioContext = AudioContext | null
type MaybeAudioSourceNode = MediaStreamAudioSourceNode | null

export const useCallSettingsStore = defineStore('callSettings', () => {
  const audioInputDeviceId = useLocalStorage<string | null>('call-audio-input-device-id', null)
  const audioOutputDeviceId = useLocalStorage<string | null>('call-audio-output-device-id', null)
  const videoInputDeviceId = useLocalStorage<string | null>('call-video-input-device-id', null)

  const microphoneGain = useLocalStorage<number>('call-microphone-gain', 1)
  const outputVolume = useLocalStorage<number>('call-output-volume', 1)

  const isCameraEnabled = useLocalStorage<boolean>('call-is-camera-enabled', false)
  const isMicrophoneEnabled = useLocalStorage<boolean>('call-is-microphone-enabled', true)
  const isOutputAudioEnabled = useLocalStorage<boolean>('call-is-output-audio-enabled', true)

  const videoFilter = useLocalStorage<CallVideoFilterType>('call-video-filter', CallVideoFilterType.None)
  const selectedBackgroundId = useLocalStorage<string | null>('call-video-background-id', null)

  const callLayout = useLocalStorage<CallLayoutType>('call-layout', CallLayoutType.Auto)
  const areCaptionsEnabled = useLocalStorage<boolean>('call-are-captions-enabled', false)

  const isPreviewActive = ref<boolean>(false)
  const isCallActive = ref<boolean>(false)

  const cameraTrack = shallowRef<LocalVideoTrack | null>(null)
  const cameraTrackVersion = ref<number>(0)

  const microphoneTrack = shallowRef<LocalAudioTrack | null>(null)
  const microphoneTrackVersion = ref<number>(0)
  const microphoneRawTrack = ref<MediaStreamTrack | null>(null)
  const microphoneAudioContext = ref<MaybeAudioContext>(null)
  const microphoneAudioSourceNode = ref<MaybeAudioSourceNode>(null)
  const microphoneAnalyserNode = ref<AnalyserNode | null>(null)
  const microphoneGainNode = ref<GainNode | null>(null)
  const microphoneInputLevel = ref<number>(0)
  const microphoneLevelRafId = ref<number | null>(null)

  const outputVolumeClamped = computed(() => {
    if (Number.isNaN(outputVolume.value)) {
      return 1
    }
    return Math.max(0, Math.min(1, outputVolume.value))
  })

  const microphoneGainClamped = computed(() => {
    if (Number.isNaN(microphoneGain.value)) {
      return 1
    }
    return Math.max(0, Math.min(1, microphoneGain.value))
  })

  const shouldCaptureCamera = computed(() => {
    return Boolean(isCameraEnabled.value && (isPreviewActive.value || isCallActive.value))
  })

  const shouldCaptureMicrophone = computed(() => {
    return Boolean(isPreviewActive.value || isCallActive.value)
  })

  const canUseVideoFilters = supportsStabilizedBackgroundProcessor()
  let videoProcessor: ReturnType<typeof createStabilizedBackgroundProcessor> | null = null
  const isVideoProcessorAttached = ref(false)

  let cameraQueue: Promise<void> = Promise.resolve()
  let microphoneQueue: Promise<void> = Promise.resolve()
  let pendingCameraTrackPromise: Promise<void> | null = null
  let pendingMicrophoneTrackPromise: Promise<void> | null = null

  function getVideoProcessor() {
    if (!canUseVideoFilters) {
      return null
    }
    if (!videoProcessor) {
      videoProcessor = createStabilizedBackgroundProcessor({mode: 'disabled'})
    }
    return videoProcessor
  }

  async function ensureVideoProcessorAttached(track: LocalVideoTrack) {
    const processor = getVideoProcessor()
    if (!processor || isVideoProcessorAttached.value) {
      return processor
    }
    await track.setProcessor(processor)
    isVideoProcessorAttached.value = true
    return processor
  }

  async function applyVideoFilterToProcessor(track: LocalVideoTrack | null = cameraTrack.value) {
    if (!track) {
      return
    }
    const processor = getVideoProcessor()
    if (!processor) {
      return
    }
    if (videoFilter.value === CallVideoFilterType.None) {
      if (!isVideoProcessorAttached.value) {
        return
      }
      await processor.switchTo({mode: 'disabled'})
      return
    }
    await ensureVideoProcessorAttached(track)
    if (videoFilter.value === CallVideoFilterType.BackgroundBlur) {
      await processor.switchTo({
        mode: 'background-blur',
        blurRadius: 32,
      })
      return
    }
    if (videoFilter.value === CallVideoFilterType.VirtualBackground) {
      const backgroundStore = useCallBackgroundStore()
      const imagePath = await backgroundStore.getImageUrl(selectedBackgroundId.value)
      if (!imagePath) {
        await processor.switchTo({mode: 'disabled'})
        return
      }
      await processor.switchTo({
        mode: 'virtual-background',
        imagePath,
      })
    }
  }

  function isMediaTrackEnded(track: LocalVideoTrack | LocalAudioTrack | null) {
    return Boolean(track) && track.mediaStreamTrack?.readyState === 'ended'
  }

  function dropEndedCameraTrack() {
    if (!isMediaTrackEnded(cameraTrack.value)) {
      return
    }
    cameraTrack.value = null
    isVideoProcessorAttached.value = false
    videoProcessor = null
    cameraTrackVersion.value += 1
  }

  async function ensureCameraTrack() {
    if (!shouldCaptureCamera.value) {
      return
    }
    dropEndedCameraTrack()
    if (cameraTrack.value) {
      return
    }
    if (pendingCameraTrackPromise) {
      await pendingCameraTrackPromise.catch(() => undefined)
      return
    }
    pendingCameraTrackPromise = (async () => {
      const deviceId = videoInputDeviceId.value ?? undefined
      const track = await createLocalVideoTrack({
        deviceId,
        facingMode: 'user',
      })
      track.source = Track.Source.Camera
      if (videoFilter.value !== CallVideoFilterType.None) {
        try {
          await applyVideoFilterToProcessor(track)
        } catch (_error) {
          isVideoProcessorAttached.value = false
        }
      }
      cameraTrack.value = track
      cameraTrackVersion.value += 1
    })()
    try {
      await pendingCameraTrackPromise
    } catch (error) {
      isCameraEnabled.value = false
      // eslint-disable-next-line no-console
      console.warn('Could not acquire camera track, joining without video', error)
    } finally {
      pendingCameraTrackPromise = null
    }
  }

  async function stopCameraTrack() {
    if (!cameraTrack.value) {
      return
    }
    const track = cameraTrack.value
    const wasProcessorAttached = isVideoProcessorAttached.value
    cameraTrack.value = null
    isVideoProcessorAttached.value = false
    cameraTrackVersion.value += 1
    try {
      if (wasProcessorAttached) {
        await track.stopProcessor()
      }
    } catch (error) {
      void error
    }
    try {
      await track.stop()
    } catch (error) {
      void error
    }
    videoProcessor = null
  }

  async function createProcessedMicrophoneTrack() {
    const deviceId = audioInputDeviceId.value ?? undefined
    const track = await createLocalAudioTrack({
      deviceId,
    })
    track.source = Track.Source.Microphone
    microphoneRawTrack.value = track.mediaStreamTrack
    microphoneAudioContext.value = null
    microphoneAudioSourceNode.value = null
    microphoneAnalyserNode.value = null
    microphoneGainNode.value = null
    stopMicrophoneLevelLoop()
    startMicrophoneAnalysis(track.mediaStream)
    return track
  }

  function stopMicrophoneLevelLoop() {
    if (microphoneLevelRafId.value !== null) {
      cancelAnimationFrame(microphoneLevelRafId.value)
      microphoneLevelRafId.value = null
    }
    microphoneInputLevel.value = 0
  }

  function startMicrophoneLevelLoop(analyser: AnalyserNode) {
    stopMicrophoneLevelLoop()
    const buffer = new Uint8Array(analyser.fftSize)
    const tick = () => {
      analyser.getByteTimeDomainData(buffer)
      let sum = 0
      for (let i = 0; i < buffer.length; i += 1) {
        const v = (buffer[i] - 128) / 128
        sum += v * v
      }
      const rms = Math.sqrt(sum / buffer.length)
      microphoneInputLevel.value = Math.max(0, Math.min(1, rms * 2.5))
      microphoneLevelRafId.value = requestAnimationFrame(tick)
    }
    microphoneLevelRafId.value = requestAnimationFrame(tick)
  }

  function ensureAudioContextRunning(audioContext: AudioContext) {
    const tryResume = () => {
      void audioContext.resume().catch(() => undefined)
    }
    tryResume()
    if (audioContext.state === 'running') {
      return
    }
    const stopListeners: (() => void)[] = []
    const cleanup = () => {
      stopListeners.forEach(stopListener => stopListener())
    }
    stopListeners.push(
      useEventListener(window, 'pointerdown', tryResume),
      useEventListener(window, 'keydown', tryResume),
      useEventListener(audioContext, 'statechange', () => {
        if (audioContext.state === 'running' || audioContext.state === 'closed') {
          cleanup()
        }
      }),
    )
  }

  function startMicrophoneAnalysis(mediaStream: MediaStream | null) {
    if (!mediaStream) {
      microphoneInputLevel.value = 0
      return
    }

    const AudioContextCtor = window.AudioContext || (window as typeof window & {webkitAudioContext?: typeof AudioContext}).webkitAudioContext
    if (!AudioContextCtor) {
      microphoneInputLevel.value = 0
      return
    }

    const previousSourceNode = microphoneAudioSourceNode.value
    const previousAnalyserNode = microphoneAnalyserNode.value
    const previousAudioContext = microphoneAudioContext.value

    try {
      previousSourceNode?.disconnect()
    } catch (_error) {
      void _error
    }

    try {
      previousAnalyserNode?.disconnect()
    } catch (_error) {
      void _error
    }

    if (previousAudioContext) {
      void previousAudioContext.close().catch(() => undefined)
    }

    const audioContext = new AudioContextCtor()
    const sourceNode = audioContext.createMediaStreamSource(mediaStream)
    const analyserNode = audioContext.createAnalyser()
    analyserNode.fftSize = 2048
    sourceNode.connect(analyserNode)
    ensureAudioContextRunning(audioContext)

    microphoneAudioContext.value = audioContext
    microphoneAudioSourceNode.value = sourceNode
    microphoneAnalyserNode.value = analyserNode
    startMicrophoneLevelLoop(analyserNode)
  }

  async function ensureMicrophoneTrack() {
    if (!shouldCaptureMicrophone.value) {
      return
    }
    if (isMediaTrackEnded(microphoneTrack.value)) {
      await stopMicrophoneTrack()
    }
    if (microphoneTrack.value) {
      return
    }
    if (pendingMicrophoneTrackPromise) {
      await pendingMicrophoneTrackPromise.catch(() => undefined)
      return
    }
    pendingMicrophoneTrackPromise = (async () => {
      const track = await createProcessedMicrophoneTrack()
      microphoneTrack.value = track
      microphoneTrackVersion.value += 1
    })()
    try {
      await pendingMicrophoneTrackPromise
    } catch (error) {
      isMicrophoneEnabled.value = false
      // eslint-disable-next-line no-console
      console.warn('Could not acquire microphone track, joining without audio', error)
    } finally {
      pendingMicrophoneTrackPromise = null
    }
  }

  async function stopMicrophoneTrack() {
    const track = microphoneTrack.value
    const raw = microphoneRawTrack.value
    const audioContext = microphoneAudioContext.value

    microphoneTrack.value = null
    microphoneTrackVersion.value += 1
    microphoneRawTrack.value = null
    microphoneAudioSourceNode.value = null
    microphoneAnalyserNode.value = null
    microphoneAudioContext.value = null
    microphoneGainNode.value = null
    stopMicrophoneLevelLoop()

    try {
      if (track) {
        await track.stop()
      }
    } catch (error) {
      void error
    }

    try {
      if (raw) {
        raw.stop()
      }
    } catch (error) {
      void error
    }

    try {
      microphoneAudioSourceNode.value?.disconnect()
    } catch (error) {
      void error
    }

    try {
      microphoneAnalyserNode.value?.disconnect()
    } catch (error) {
      void error
    }

    try {
      if (audioContext) {
        await audioContext.close()
      }
    } catch (error) {
      void error
    }
  }

  function reconcileCapture() {
    cameraQueue = cameraQueue.then(async () => {
      if (shouldCaptureCamera.value) {
        await ensureCameraTrack()
        return
      }
      await stopCameraTrack()
    })
    microphoneQueue = microphoneQueue.then(async () => {
      if (shouldCaptureMicrophone.value) {
        await ensureMicrophoneTrack()
        return
      }
      await stopMicrophoneTrack()
    })
  }

  function setPreviewActive(value: boolean) {
    isPreviewActive.value = value
    if (value) {
      reconcileCapture()
    }
  }

  function setCallActive(value: boolean) {
    isCallActive.value = value
  }

  function setCameraEnabled(value: boolean) {
    isCameraEnabled.value = value
  }

  function toggleCamera() {
    setCameraEnabled(!isCameraEnabled.value)
  }

  function setMicrophoneEnabled(value: boolean) {
    isMicrophoneEnabled.value = value
  }

  function toggleMicrophone() {
    setMicrophoneEnabled(!isMicrophoneEnabled.value)
  }

  const toggleGlobalOutputAudioStatus = () => {
    isOutputAudioEnabled.value = !isOutputAudioEnabled.value
  }

  function setVideoFilter(value: CallVideoFilterType) {
    videoFilter.value = value
  }

  function setVideoBackground(id: string) {
    selectedBackgroundId.value = id
    videoFilter.value = CallVideoFilterType.VirtualBackground
  }

  function setCallLayout(value: CallLayoutType) {
    callLayout.value = value
  }

  function toggleCaptions() {
    areCaptionsEnabled.value = !areCaptionsEnabled.value
  }

  const cameraMediaStream = computed<MediaStream | null>(() => {
    return cameraTrack.value?.mediaStream ?? null
  })

  function getCameraTrack(): LocalVideoTrack | null {
    return cameraTrack.value
  }

  function getMicrophoneTrack(): LocalAudioTrack | null {
    return microphoneTrack.value
  }

  watch(shouldCaptureCamera, () => {
    cameraQueue = cameraQueue.then(async () => {
      if (shouldCaptureCamera.value) {
        await ensureCameraTrack()
      } else {
        await stopCameraTrack()
      }
    })
  }, {immediate: true})

  watch([videoInputDeviceId], () => {
    cameraQueue = cameraQueue.then(async () => {
      if (cameraTrack.value && shouldCaptureCamera.value) {
        await stopCameraTrack()
        await ensureCameraTrack()
      }
    })
  })

  watch([videoFilter, selectedBackgroundId], ([filter], [previousFilter]) => {
    if (filter === previousFilter && filter !== CallVideoFilterType.VirtualBackground) {
      return
    }
    cameraQueue = cameraQueue.then(async () => {
      if (!cameraTrack.value) {
        return
      }
      try {
        await applyVideoFilterToProcessor()
      } catch (_error) {
        isVideoProcessorAttached.value = false
      }
    })
  }, {immediate: true})

  watch(shouldCaptureMicrophone, () => {
    microphoneQueue = microphoneQueue.then(async () => {
      if (shouldCaptureMicrophone.value) {
        await ensureMicrophoneTrack()
      } else {
        await stopMicrophoneTrack()
      }
    })
  }, {immediate: true})

  watch([audioInputDeviceId], () => {
    microphoneQueue = microphoneQueue.then(async () => {
      if (microphoneTrack.value && shouldCaptureMicrophone.value) {
        await stopMicrophoneTrack()
        await ensureMicrophoneTrack()
      }
    })
  })

  watch(microphoneGainClamped, (value) => {
    const node = microphoneGainNode.value
    if (!node) {
      return
    }
    node.gain.value = value
  })

  return {
    audioInputDeviceId,
    audioOutputDeviceId,
    videoInputDeviceId,

    microphoneGain,
    outputVolume,

    isCameraEnabled,
    isMicrophoneEnabled,
    isOutputAudioEnabled,
    videoFilter,
    selectedBackgroundId,
    canUseVideoFilters,
    callLayout,
    areCaptionsEnabled,

    isPreviewActive,
    isCallActive,
    cameraTrackVersion,
    microphoneTrackVersion,
    cameraMediaStream,
    microphoneInputLevel,

    outputVolumeClamped,
    microphoneGainClamped,

    setPreviewActive,
    setCallActive,
    setCameraEnabled,
    toggleCamera,
    setMicrophoneEnabled,
    toggleMicrophone,
    toggleGlobalOutputAudioStatus,
    setVideoFilter,
    setVideoBackground,
    setCallLayout,
    toggleCaptions,

    ensureCameraTrack,
    stopCameraTrack,
    ensureMicrophoneTrack,
    stopMicrophoneTrack,

    getCameraTrack,
    getMicrophoneTrack,
  }
})
